import { useSyncExternalStore } from 'react';

/**
 * A short click for every press and every mark in the staff app (owner,
 * 2026-09-08: "whenever i press something or i mark something i want there
 * to be a little click sound").
 *
 * One document-level listener rather than a prop on every button: the app
 * has hundreds of controls across a dozen screens, and a sound that depended
 * on each one remembering to ask for it would be missing from half of them
 * within a month. The listener decides from the element what was pressed — a
 * button, a link, a checkbox, a tab, a menu item, a select — and stays
 * silent for typing, for disabled controls, and for anything that is not a
 * control at all.
 *
 * Synthesised through the Web Audio API, as the assist beep is: no audio
 * file to ship, cache, or fail to load. ONE context for the life of the page,
 * created inside the first click — browsers only let audio start from a
 * gesture, and a click is one — rather than a context per click, which is
 * what a hundred presses an hour would otherwise open and close.
 *
 * Per-terminal preference in localStorage, on by default; the speaker button
 * in the top bar turns it off for this PC only.
 */

export const CLICK_SOUND_KEY = 'gc.staff.clickSound';

/**
 * Two clicks inside this window are ONE press. A label forwards its click to
 * the checkbox it wraps, and a select fires click and change for one choice;
 * neither deserves two sounds.
 */
export const DEDUPE_MS = 60;

const CONTROL_ROLES = new Set([
  'button',
  'tab',
  'menuitem',
  'menuitemcheckbox',
  'menuitemradio',
  'switch',
  'checkbox',
  'radio',
  'option',
  'link',
]);

/** Inputs that are pressed rather than typed into. */
const PRESSED_INPUT_TYPES = new Set([
  'checkbox',
  'radio',
  'button',
  'submit',
  'reset',
  'range',
  'color',
  'file',
]);

/** What the listener knows about the element under the pointer. */
export interface PressedElement {
  tag: string;
  type?: string | null;
  role?: string | null;
  href?: boolean;
  disabled?: boolean;
  editable?: boolean;
}

/**
 * Is this a control somebody PRESSES? Pure, so the table of cases can be
 * pinned without a DOM.
 *
 * An explicit role decides on its own: a `div role="button"` is a button and
 * a `button role="presentation"` is not. Without one the tag decides; text
 * inputs, textareas and anything editable are typed into, not pressed, and a
 * disabled control did nothing, so it sounds like nothing.
 */
export function isPress(element: PressedElement): boolean {
  if (element.disabled || element.editable) return false;

  const role = (element.role ?? '').trim().toLowerCase();
  if (role) return CONTROL_ROLES.has(role);

  switch (element.tag.toLowerCase()) {
    case 'button':
    case 'select':
    case 'summary':
    case 'option':
      return true;
    case 'a':
      return element.href === true;
    case 'input':
      return PRESSED_INPUT_TYPES.has((element.type ?? 'text').trim().toLowerCase());
    default:
      return false;
  }
}

/** On unless this PC said off. A storage that refuses to answer is "on". */
export function readClickSoundEnabled(storage: Pick<Storage, 'getItem'> | null): boolean {
  try {
    return storage?.getItem(CLICK_SOUND_KEY) !== '0';
  } catch {
    return true;
  }
}

/** The one gate every play goes through: the preference, and the dedupe. */
export function shouldPlay(input: { enabled: boolean; now: number; lastPlayedAt: number }): boolean {
  return input.enabled && input.now - input.lastPlayedAt >= DEDUPE_MS;
}

/* -------------------------------------------------------------------------- */
/* The preference                                                             */
/* -------------------------------------------------------------------------- */

function safeStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

let enabled = readClickSoundEnabled(safeStorage());
const listeners = new Set<() => void>();

export function isClickSoundEnabled(): boolean {
  return enabled;
}

export function setClickSoundEnabled(next: boolean): void {
  enabled = next;
  try {
    safeStorage()?.setItem(CLICK_SOUND_KEY, next ? '1' : '0');
  } catch {
    // Storage refused; the choice still holds for this page.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** For the top bar's speaker button. */
export function useClickSoundEnabled(): boolean {
  return useSyncExternalStore(subscribe, isClickSoundEnabled, () => true);
}

/* -------------------------------------------------------------------------- */
/* The sound                                                                  */
/* -------------------------------------------------------------------------- */

let context: AudioContext | null = null;

function audioContext(): AudioContext | null {
  if (context) return context;
  const Ctor =
    window.AudioContext ??
    (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  context = new Ctor();
  return context;
}

/**
 * The owner's pick from fifteen auditioned (2026-09-08): "Chirp Up" — a
 * single sine rising from 700 Hz to 1.5 kHz over forty-five milliseconds,
 * quiet, gone before the next one. It reads as "done" rather than as a
 * mechanical clack. Failure is swallowed for the same reason the assist
 * beep's is: no sound is an acceptable outcome, a crashed till is not.
 */
export function playClick(): void {
  try {
    const audio = audioContext();
    if (!audio) return;
    // A page that started without a gesture holds its context suspended;
    // the press that got us here is the gesture that lifts it.
    if (audio.state === 'suspended') void audio.resume().catch(() => {});

    const now = audio.currentTime;
    const gain = audio.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.12, now + 0.003);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.045);
    gain.connect(audio.destination);

    const oscillator = audio.createOscillator();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(700, now);
    oscillator.frequency.exponentialRampToValueAtTime(1500, now + 0.045);
    oscillator.connect(gain);
    oscillator.start(now);
    oscillator.stop(now + 0.055);
  } catch {
    // Silence is fine.
  }
}

/* -------------------------------------------------------------------------- */
/* The listener                                                               */
/* -------------------------------------------------------------------------- */

const CONTROL_SELECTOR = 'button, a[href], input, select, summary, option, [role]';

function describe(target: EventTarget | null): PressedElement | null {
  if (!(target instanceof Element)) return null;
  const element = target.closest(CONTROL_SELECTOR);
  if (!(element instanceof HTMLElement)) return null;
  return {
    tag: element.tagName,
    type: element.getAttribute('type'),
    role: element.getAttribute('role'),
    href: element.hasAttribute('href'),
    disabled: element.matches(':disabled') || element.getAttribute('aria-disabled') === 'true',
    editable: element.isContentEditable,
  };
}

let lastPlayedAt = Number.NEGATIVE_INFINITY;

function playForPress(): void {
  const now = performance.now();
  if (!shouldPlay({ enabled, now, lastPlayedAt })) return;
  lastPlayedAt = now;
  playClick();
}

/**
 * Attach once, at startup. Capture phase, so a handler that stops
 * propagation (dialogs and menus do) cannot silence the press it handled;
 * `isTrusted`, so a click the app dispatches itself is not a press.
 *
 * `change` covers the one press that arrives without a click: a select
 * changed from the keyboard. Everything else — including a checkbox toggled
 * with the space bar — reaches us as a click.
 */
export function installClickSound(root: Document = document): () => void {
  const onClick = (event: Event) => {
    if (!event.isTrusted) return;
    const pressed = describe(event.target);
    if (!pressed || !isPress(pressed)) return;
    playForPress();
  };
  const onChange = (event: Event) => {
    if (!event.isTrusted) return;
    if (!(event.target instanceof HTMLSelectElement) || event.target.disabled) return;
    playForPress();
  };

  root.addEventListener('click', onClick, true);
  root.addEventListener('change', onChange, true);
  return () => {
    root.removeEventListener('click', onClick, true);
    root.removeEventListener('change', onChange, true);
  };
}
